/**
 * **The damage packet** (`event-engine-damage` D1, `decisions/0212` item 1): everything one source
 * deals one target at once, as one event. Its instances are the separate damage types and dice of
 * that damage (a weapon's primary and its riders), each already rolled; mitigation runs on the
 * whole packet in the order RAW fixes (`resolve/damagePacket.ts`, D2). Its cause is whatever dealt
 * it: the mine's detonation, the move a bulldoze rode, the trigger an atom fired from.
 *
 * Raising a packet never draws a die. Every instance is rolled before the packet is proposed, so
 * the draw order is the site's own and unchanged.
 */
import type { DamageType } from "@lancer-vtt/domain";
import type { Cause, EventLoop } from "./loop";

/** One instance of a packet: a damage type and its pre-mitigation amount, with the dice that made it. */
export type DamageInstance = {
  dtype: DamageType;
  /** The rolled dice plus the bonus, before Exposed or any mitigation. */
  amount: number;
  /** Every kept die face; absent for flat damage. */
  dice?: number[];
  sides?: number;
  /** The flat part of `amount`. Absent: all of `amount` (flat damage). */
  bonus?: number;
  crit?: boolean;
  /** Resistance already halved it upstream (an attack's heat, mitigated as heat before it became energy), so the packet doesn't apply Resistance again. */
  resisted?: true;
};

export type DamagePacketEvent = {
  kind: "damage_packet";
  /** The character who dealt it, or `null` when none did (terrain, the burn tick, the GM). Attacker-relative grants fold against it. */
  source: string | null;
  target: string;
  instances: DamageInstance[];
  /** Is this damage from an attack? Attack-gated grants (Siege Armor) fold only then. */
  fromAttack: boolean;
  /** Armor-piercing: the whole packet ignores Armor. */
  ap: boolean;
  /** Other deductions (Leadership Die, Inspiring Presence), taken once from the packet after Resistance (Core p.67 step 3). */
  reduce?: number;
  /** "Can't be prevented or reduced in any way" (External Batteries): no Armor, Resistance, Reduce or Immunity. Overshield still takes it first. */
  irreducible?: true;
  /**
   * Sniper's Mark (Core p.309): the packet deals 1 structure damage instead of its instances. No
   * Armor, Resistance, Overshield or HP: the structure point goes through the damage cascade's
   * substitution, so the structure-loss window (Custom Paint Job) still sees it.
   */
  substituteStructure?: true;
};

/** Named args for {@link raiseDamagePacket}. */
export type RaiseDamagePacketArgs<R> = {
  /** The intent's loop. Absent (a site not yet threaded one, a forecast, a unit test), the packet lands with no event. */
  loop: EventLoop | undefined;
  packet: DamagePacketEvent;
  /** Grounds the packet as it lands: the pipeline over the state it lands on. */
  land: (packet: DamagePacketEvent) => R;
  cause?: Cause;
};

/**
 * Raise a {@link DamagePacketEvent}: "happens" runs `land`, the site's pipeline call. Returns what
 * `land` returned. With no loop, the packet lands directly, as `drawRoll` draws.
 */
export function raiseDamagePacket<R>(args: RaiseDamagePacketArgs<R>): R {
  const { loop, packet, land, cause } = args;
  if (loop === undefined) return land(packet);
  let landed: { result: R } | undefined;
  loop.raise(
    packet,
    {
      happen: (event) => {
        if (event.kind !== "damage_packet") throw new Error(`raiseDamagePacket: a replacement turned a damage packet into ${event.kind}`);
        landed = { result: land(event) };
      },
      trigger: () => undefined,
    },
    cause,
  );
  if (landed === undefined) throw new Error("raiseDamagePacket: a replacement cancelled a damage packet, which nothing may do yet");
  return landed.result;
}
